/* Fund Dashboard — ported from the prototype's Screens.dashboard(). Live deals,
   pipeline ask vs funds, avg decision time, active diligence, conversion funnel,
   inbound by sector, rejection reasons, inbound-quality trend, governance stats,
   a stagnant-deal nudge and an Intelligentsia insight. Every figure is computed
   by the backend from the firm's deals + FCA audit log (GET /deal/dashboard). */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import Bubble from '../components/Bubble';
import BarRow from '../components/BarRow';
import { useToast } from '../components/Toast';
import { useProfile } from '../state';
import { Loading, ErrorState, EmptyState } from '../components/States';
import { BASE } from '../helpers';
import { fetchDashboard } from '../api';

const BLUES = ['#4A67FF', '#6B82FF', '#8C9DFF', '#B3BEFF', 'var(--dlg-green)'];

export default function Dashboard() {
	const navigate = useNavigate();
	const showToast = useToast();
	const { profile } = useProfile();
	const [D, setD] = useState(null);
	const [error, setError] = useState(false);

	const load = useCallback(async () => {
		setError(false);
		const data = await fetchDashboard();
		if (!data) { setError(true); setD(null); return; }
		setD(data);
	}, []);
	useEffect(() => { load(); }, [load]);

	if (error) return <main className="page screen"><ErrorState title="Could not load the dashboard" onRetry={load} /></main>;
	if (D === null) return <main className="page screen"><Loading label="Loading dashboard…" /></main>;

	const newThisWeek = D.newThisWeek || [];
	const funnel = D.funnel || [];
	const sectors = D.sectors || [];
	const rejections = D.rejections || [];
	const governance = D.governance || [];
	const active = D.activeDiligence || { count: 0, allocation: '£0', detail: '—' };
	const quality = D.inboundQuality || { score: 0, best: '—', worst: '—' };
	const received = funnel[0] ? funnel[0].n : 0;

	return (
		<main className="page screen">
			<div className="page__head">
				<div>
					<div className="kicker">Fund dashboard · {(profile && profile.short) || 'Your fund'}</div>
					<div className="page__title">Pipeline pulse</div>
				</div>
				<div className="page__actions">
					<button className="pill pill--ghost">Last 12 months ▾</button>
					<button className="pill pill--outline" onClick={() => navigate(`${BASE}/pipeline`)}>Open pipeline →</button>
				</div>
			</div>

			<div className="grid grid--4">
				<div className="card">
					<div className="card__label">Deal flow</div>
					<div className="stat">{D.liveDeals} <span className="stat__unit">live deals</span></div>
					<div className="stat__note stat__note--up">▲ {newThisWeek.length} new this week{newThisWeek.length ? ` · ${newThisWeek.join(', ')}` : ''}</div>
				</div>
				<div className="card">
					<div className="card__label">Pipeline ask</div>
					<div className="stat">{D.pipelineAsk} <span className="stat__unit">across {D.liveDeals}</span></div>
					<div className="stat__note">vs {D.fundsThisVintage} funds available this vintage</div>
				</div>
				<div className="card">
					<div className="card__label">Avg decision time</div>
					<div className="stat">{D.avgDecisionDays} days</div>
					<div className="stat__note">{D.stageDays}</div>
				</div>
				<div className="card">
					<div className="card__label">Active diligence</div>
					<div className="stat">{active.count} <span className="stat__unit">· {active.allocation} allocation</span></div>
					<div className="stat__note">{active.detail}</div>
				</div>
			</div>

			<div className="grid grid--split">
				<div className="card">
					<div className="card__row" style={{ marginBottom: 12 }}><div className="card__label">Conversion funnel</div><span className="card__hint">{received} applications</span></div>
					<div style={{ display: 'grid', gap: 7 }}>
						{funnel.map((f, i) => (
							<div key={f.label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
								<span style={{ width: 118, fontSize: 13, color: 'var(--dlg-ink-soft)' }}>{f.label}</span>
								<div style={{ flex: 1, height: 16, borderRadius: 999, background: 'var(--dlg-fill)' }}><div style={{ width: `${f.pct}%`, height: 16, borderRadius: 999, background: BLUES[i] }} /></div>
								<span style={{ width: 110, textAlign: 'right', fontWeight: 500, fontSize: 13 }}>
									{i === 0 ? f.n : <>{f.n} <span style={{ fontWeight: 400, color: 'var(--dlg-faint)' }}>{f.pct}%{f.extra ? ` · ${f.extra}` : ''}</span></>}
								</span>
							</div>
						))}
					</div>
					<div className="card__foot">
						<span><strong>{D.gateDeclined}</strong> auto-declined at gate · zero AI spend</span>
						<span><strong>{D.totalPasses}</strong> total passes</span>
						<span><strong>{D.liveDeals}</strong> still live</span>
					</div>
				</div>
				<div className="card">
					<div className="card__label" style={{ marginBottom: 12 }}>Inbound by sector</div>
					{sectors.length === 0 ? <div style={{ fontSize: 13, color: 'var(--dlg-muted)' }}>No sector data yet.</div> : (
						<div style={{ display: 'grid', gap: 8 }}>
							{sectors.map((s, i) => (
								<div key={s.name} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
									<span style={{ width: 92, fontSize: 13, color: 'var(--dlg-ink-soft)' }}>{s.name}</span>
									<div style={{ flex: 1, height: 9, borderRadius: 999, background: 'var(--dlg-fill)' }}><div style={{ width: `${s.pct}%`, height: 9, borderRadius: 999, background: i === 3 ? 'var(--dlg-coral)' : BLUES[Math.min(i, 3)] }} /></div>
									<span style={{ width: 34, textAlign: 'right', fontWeight: 500, fontSize: 13 }}>{s.pct}%</span>
								</div>
							))}
						</div>
					)}
					<div className="card__foot">Live share of inbound across your thesis sectors</div>
				</div>
			</div>

			<div className="grid grid--3a">
				<div className="card">
					<div className="card__row" style={{ marginBottom: 12 }}><div className="card__label">Rejection reasons</div><span className="card__hint">{D.totalPasses} passes</span></div>
					{rejections.length === 0 ? <div style={{ fontSize: 13, color: 'var(--dlg-muted)' }}>No declines logged yet.</div> : (
						<div style={{ display: 'grid', gap: 9 }}>
							{rejections.map((r, i) => (
								<BarRow key={r.reason} label={r.reason} right={`${r.n} · ${r.pct}%`} pct={r.pct}
									color={i === 3 ? 'var(--dlg-coral)' : i === 4 ? 'var(--dlg-border-soft)' : BLUES[Math.min(i, 2)]} />
							))}
						</div>
					)}
				</div>
				<div className="card" style={{ display: 'flex', flexDirection: 'column' }}>
					<div className="card__label">Inbound quality</div>
					<div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 5 }}>
						<span className="stat">{quality.score}<span style={{ fontSize: 16, color: 'var(--dlg-faint)' }}>/100</span></span>
						<span className="chip chip--green">● healthy</span>
					</div>
					<div className="stat__note">Avg analysis score · best {quality.best} · worst {quality.worst}</div>
					<svg viewBox="0 0 260 58" style={{ width: '100%', marginTop: 'auto' }} aria-hidden="true">
						<polyline points="6,42 46,37 86,40 126,27 166,31 206,20 254,16" fill="none" stroke="var(--dlg-blue)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
						<circle cx="254" cy="16" r="4.5" fill="var(--dlg-blue)" />
					</svg>
					<div style={{ display: 'flex', fontSize: 11.5, color: 'var(--dlg-axis)' }}><span>Mar</span><span style={{ marginLeft: 'auto' }}>Aug</span></div>
				</div>
				<div className="card">
					<div className="card__label" style={{ marginBottom: 10 }}>Governance &amp; network</div>
					<div style={{ display: 'grid', gap: 10 }}>
						{governance.map(g => (
							<div key={g.label} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'var(--dlg-ink-soft)' }}>
								<span style={{ width: 8, height: 8, borderRadius: '50%', background: g.dot, flex: 'none' }} />
								<span>{g.label}</span><span style={{ marginLeft: 'auto', fontWeight: 500, color: 'var(--dlg-ink)' }}>{g.value}</span>
							</div>
						))}
					</div>
				</div>
			</div>

			<div className="card">
				<div className="card__row" style={{ marginBottom: 10 }}>
					<div className="card__label">Stagnant deals · over 7 days in stage</div>
					{D.stagnant ? <span className="chip chip--amber" style={{ marginLeft: 'auto' }}>1 needs action</span> : <span className="chip chip--green" style={{ marginLeft: 'auto' }}>all moving</span>}
				</div>
				{D.stagnant ? (
					<div className="stagnant">
						<span style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--dlg-amber)', flex: 'none' }} />
						<div>
							<div style={{ fontSize: 14, fontWeight: 500 }}>{D.stagnant.name} <span style={{ fontWeight: 400, color: 'var(--dlg-muted)' }}>· {D.stagnant.meta}</span></div>
							<div style={{ fontSize: 12.5, color: 'var(--dlg-muted)' }}>{D.stagnant.detail}</div>
						</div>
						<button className="pill pill--ghost" style={{ marginLeft: 'auto' }} onClick={() => showToast(`Nudge drafted for ${D.stagnant.name} — send from the Diligence workspace`)}>Nudge founder →</button>
					</div>
				) : <div style={{ fontSize: 13, color: 'var(--dlg-muted)' }}>No deals have stalled — everything is moving through the pipeline.</div>}
				<div style={{ marginTop: 10 }}>
					<Bubble><strong>Intelligentsia:</strong> the gate cleared {D.totalPasses} of {received} applications before any AI spend. Governance, sector mix and rejection reasons all update live as deals move.</Bubble>
				</div>
			</div>
		</main>
	);
}
